// src/components/recharge/DecompressCard.jsx
//
// Decompress: one prompt a day, one answer, into the member's private journal
// (decompress_log). Yesterday's line shows quietly beneath; the Talk-to-
// Someone floor sits under it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PenLine, Check, Save } from 'lucide-react';
import { promptForDate, todayKey } from '../../data/winddown';
import { subscribeJournalEntry, saveJournalEntry } from '../../utils/journalStore';
import SupportOutlet from '../dashboard/SupportOutlet';

const LOG_ID = 'decompress_log';

const DecompressCard = ({ uid, currentUser }) => {
    const [log, setLog] = useState(null);
    const [text, setText] = useState('');
    const [state, setState] = useState('idle'); // idle | pending | saved
    const timer = useRef(null);
    const loaded = useRef(false);

    const prompt = useMemo(() => promptForDate(new Date()), []);
    const key = todayKey();

    useEffect(() => {
        if (!uid) return undefined;
        const unsub = subscribeJournalEntry(uid, LOG_ID, (e) => {
            setLog(e);
            if (!loaded.current) {
                const today = (e?.entries || []).find(x => x.day === key);
                if (today) setText(today.text || '');
                loaded.current = true;
            }
        });
        return () => unsub();
    }, [uid, key]);

    const entries = useMemo(() => (Array.isArray(log?.entries) ? log.entries : []), [log]);
    const previous = useMemo(() => entries.filter(e => e.day !== key).slice(-1)[0] || null, [entries, key]);

    const save = async (value) => {
        if (!uid) return;
        const others = entries.filter(e => e.day !== key);
        const next = [...others, { day: key, at: new Date().toISOString(), prompt, text: value }].slice(-60);
        try {
            await saveJournalEntry(uid, LOG_ID, { worksheetId: LOG_ID, kind: 'decompress', title: 'Decompress', topicId: 'reset-recharge', entries: next, progress: { answered: next.length, total: next.length, pct: 100 } });
            setState('saved');
        } catch (e) { console.error('decompress save failed', e); setState('idle'); }
    };
    const onChange = (v) => {
        setText(v); setState('pending');
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => save(v), 800);
    };
    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><PenLine size={22} /></div>
                <div className="min-w-0">
                    <h2 className="text-lg font-bold text-slate-800">Decompress</h2>
                    <p className="text-sm text-slate-500 mt-0.5">One question a day. A few words is plenty — only you can see it.</p>
                </div>
            </div>
            <p className="text-base font-black text-slate-900 mt-4">{prompt}</p>
            <textarea
                value={text}
                onChange={e => onChange(e.target.value)}
                placeholder="Write it down and leave it here…"
                className="w-full mt-2 p-3 border border-slate-200 rounded-xl text-base md:text-sm outline-none focus:ring-2 focus:ring-brand-primary min-h-[88px]"
            />
            <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1">
                    {state === 'saved' ? <><Check size={12} className="text-emerald-500" /> Saved</> : state === 'pending' ? <><Save size={12} /> Saving…</> : <><Save size={12} /> Saves as you type</>}
                </span>
                <SupportOutlet currentUser={currentUser} label="Talk to someone" className="text-[11px] font-bold text-slate-500 hover:text-brand-primary flex items-center gap-1" />
            </div>
            {previous && previous.text && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{previous.day} · {previous.prompt}</p>
                    <p className="text-sm text-slate-600 font-medium mt-1 line-clamp-2">{previous.text}</p>
                </div>
            )}
        </div>
    );
};

export default DecompressCard;
